(function () {
  'use strict';

  var DB_NAME = 'robopixel-design-recovery';
  var STORE_NAME = 'designs';
  var DB_VERSION = 1;
  // Match the customer-facing privacy disclosure exactly: everything is gone
  // within 72 hours, including this same-browser recovery copy.
  //
  // Was 14 days. Two reasons it changed on 2026-08-31: the stated policy is
  // now 72 hours everywhere, browser included; and past 72 hours this copy
  // was useless anyway — the S3 original is deleted on that schedule, so a
  // 14-day local copy could only restore a cart whose design no longer
  // existed. It rebuilt orders that could not be fulfilled.
  var RETENTION_MS = 72 * 60 * 60 * 1000;
  var S3_WORKER = 'https://robopixel-s3-presign.robopixel-eu.workers.dev';
  var restoring = new Map();

  function openDb() {
    return new Promise(function (resolve, reject) {
      if (!('indexedDB' in window)) { reject(new Error('indexeddb-unavailable')); return; }
      var request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = function () {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: 'key' });
        }
      };
      request.onsuccess = function () { resolve(request.result); };
      request.onerror = function () { reject(request.error || new Error('indexeddb-open-failed')); };
    });
  }

  function withStore(mode, callback) {
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var transaction = db.transaction(STORE_NAME, mode);
        var store = transaction.objectStore(STORE_NAME);
        var request = callback(store);
        request.onsuccess = function () { resolve(request.result); };
        request.onerror = function () { reject(request.error || new Error('indexeddb-request-failed')); };
        transaction.oncomplete = function () { db.close(); };
      });
    });
  }

  function sourceToBlob(source) {
    if (source instanceof Blob) return Promise.resolve(source);
    if (typeof source !== 'string' || !source) return Promise.resolve(null);
    return fetch(source, { credentials: 'omit' }).then(function (response) {
      if (!response.ok) throw new Error('asset-fetch-failed');
      return response.blob();
    }).catch(function () { return null; });
  }

  function save(key, previewSource, fullSource, source) {
    if (!key) return Promise.resolve(false);
    return Promise.all([sourceToBlob(previewSource), sourceToBlob(fullSource)]).then(function (blobs) {
      if (!blobs[0] || !blobs[1]) return false;
      return withStore('readwrite', function (store) {
        return store.put({
          key: key,
          previewBlob: blobs[0],
          fullBlob: blobs[1],
          source: source || '',
          createdAt: Date.now(),
          expiresAt: Date.now() + RETENTION_MS
        });
      }).then(function () { return true; });
    }).catch(function () { return false; });
  }

  function get(key) {
    if (!key) return Promise.resolve(null);
    return withStore('readonly', function (store) { return store.get(key); }).then(function (record) {
      if (!record || Number(record.expiresAt) <= Date.now()) {
        if (record) remove(key);
        return null;
      }
      return record;
    }).catch(function () { return null; });
  }

  function remove(key) {
    return withStore('readwrite', function (store) { return store.delete(key); }).catch(function () {});
  }

  function cleanup() {
    return openDb().then(function (db) {
      return new Promise(function (resolve) {
        var tx = db.transaction(STORE_NAME, 'readwrite');
        var request = tx.objectStore(STORE_NAME).openCursor();
        request.onsuccess = function () {
          var cursor = request.result;
          if (!cursor) return;
          if (Number(cursor.value.expiresAt) <= Date.now()) cursor.delete();
          cursor.continue();
        };
        tx.oncomplete = function () { db.close(); resolve(); };
        tx.onerror = function () { db.close(); resolve(); };
      });
    }).catch(function () {});
  }

  function upload(blob, folder) {
    return fetch(S3_WORKER + '/presign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ folder: folder, contentType: blob.type || 'image/png' })
    }).then(function (response) {
      if (!response.ok) throw new Error('presign-failed');
      return response.json();
    }).then(function (signed) {
      return fetch(signed.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': blob.type || 'image/png' },
        body: blob
      }).then(function (response) {
        if (!response.ok) throw new Error('upload-failed');
        return signed.publicUrl;
      });
    });
  }

  function setStatus(card, state) {
    if (!card) return;
    card.dataset.previewState = state;
    var status = card.querySelector('[data-design-preview-status]');
    if (!status) return;
    status.hidden = false;
    if (state === 'restoring') {
      status.textContent = status.dataset.restoringText || '';
    } else {
      status.innerHTML = status.dataset.fallbackHtml || '';
    }
  }

  function showFallback(image, card) {
    var fallback = image.dataset.fallbackSrc;
    image.onerror = null;
    image.removeAttribute('data-rp-design-preview');
    if (fallback) {
      image.src = fallback;
      // Inherit a closed drawer's visibility; an explicit visible child leaks out.
      image.style.removeProperty('visibility');
    } else {
      image.hidden = true;
    }
    setStatus(card, 'fallback');
  }

  function updateCartLine(lineKey, previewUrl, fullUrl) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var item = (cart.items || []).find(function (candidate) { return candidate.key === lineKey; });
        if (!item) throw new Error('cart-line-missing');
        var properties = Object.assign({}, item.properties || {}, {
          '_Design_Preview': previewUrl,
          '_Full_Image_URL': fullUrl,
          '_Added_At': String(Date.now())
        });
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: item.quantity, properties: properties })
        });
      }).then(function (response) {
        if (!response.ok) throw new Error('cart-update-failed');
        return response.json();
      });
  }

  function restoreImage(image) {
    var card = image.closest('[data-design-preview-card]');
    var cacheKey = image.dataset.cacheKey || (card && card.dataset.cacheKey);
    var lineKey = image.dataset.lineKey || (card && card.dataset.lineKey);
    if (!cacheKey || !lineKey) { showFallback(image, card); return Promise.resolve(false); }
    if (restoring.has(cacheKey)) return restoring.get(cacheKey);

    setStatus(card, 'restoring');
    image.style.visibility = 'hidden';
    var task = get(cacheKey).then(function (record) {
      if (!record || !record.previewBlob || !record.fullBlob) throw new Error('recovery-copy-missing');
      return Promise.all([
        upload(record.previewBlob, 'cart-mockups'),
        upload(record.fullBlob, 'cart-full-images')
      ]);
    }).then(function (urls) {
      return updateCartLine(lineKey, urls[0], urls[1]);
    }).then(function () {
      window.location.reload();
      return true;
    }).catch(function (error) {
      console.warn('[RoboPixel] Could not restore aged custom-design preview:', error);
      showFallback(image, card);
      return false;
    }).finally(function () {
      restoring.delete(cacheKey);
    });

    restoring.set(cacheKey, task);
    return task;
  }

  document.addEventListener('error', function (event) {
    var target = event.target;
    if (target && target.matches && target.matches('img[data-rp-design-preview]')) {
      restoreImage(target);
    }
  }, true);

  window.RoboPixelDesignCache = {
    save: save,
    get: get,
    remove: remove,
    restoreImage: restoreImage,
    retentionDays: 14
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cleanup, { once: true });
  } else {
    cleanup();
  }
})();
